import { useEffect, useState } from 'react';
import { useField, FieldArray } from 'formik';
import { useQuery } from '@tanstack/react-query';

import { audienceGroupQueries } from 'hooks/backend/audienceGroup';

import {
  DemographicFormData,
  getEmptyAudienceTarget,
} from '../../../../util/demographics';
import { AudienceGroupOption } from '../../../../types/domainModels';

import AddButton from 'components/common/forms/AddButton';
import FormFieldError from 'components/common/forms/FormFieldError';

import { Target } from './Target';

export const Targets = ({
  selectedAudienceGroupId,
  index,
}: {
  selectedAudienceGroupId: number;
  index: number;
}): JSX.Element => {
  const audienceGroupOptionFieldName = `audienceProfile.audienceTargets.${index}.surveyAudienceTypes`;
  const [{ value: surveyAudienceTypes }, targetsMeta] = useField<
    DemographicFormData['audienceProfile']['audienceTargets'][number]['surveyAudienceTypes']
  >(audienceGroupOptionFieldName);

  const { data: audienceGroupOptionsResponse } = useQuery(
    audienceGroupQueries.options({ audienceGroupId: selectedAudienceGroupId }),
  );

  const [audienceGroupOptions, setAudienceGroupOptions] = useState<
    AudienceGroupOption[]
  >([]);

  useEffect(() => {
    if (audienceGroupOptionsResponse) {
      setAudienceGroupOptions(audienceGroupOptionsResponse);
    }
  }, [audienceGroupOptionsResponse]);

  return (
    <FieldArray
      name={audienceGroupOptionFieldName}
      render={(arrayHelpers) => {
        return (
          <div className="w-full mt-2">
            <div>
              {surveyAudienceTypes.map((_quota, index) => {
                return (
                  <Target
                    key={index}
                    audienceGroupOptions={audienceGroupOptions}
                    index={index}
                    namePrefix={audienceGroupOptionFieldName}
                    onClickRemoveTarget={() => {
                      if (surveyAudienceTypes.length > 1) {
                        arrayHelpers.remove(index);
                      } else {
                        arrayHelpers.replace(
                          index,
                          getEmptyAudienceTarget(selectedAudienceGroupId),
                        );
                      }
                    }}
                    selectedAudienceGroupId={selectedAudienceGroupId}
                  />
                );
              })}
            </div>
            {/* This is a top-level error for all targets like the percentages not adding to 100%. */}
            {typeof targetsMeta.error === 'string' && (
              <FormFieldError error={targetsMeta.error} />
            )}
            <div className="flex mt-2">
              <AddButton
                label="Add Another Target"
                onClick={() => {
                  arrayHelpers.push(
                    getEmptyAudienceTarget(selectedAudienceGroupId),
                  );
                }}
              />
            </div>
          </div>
        );
      }}
    />
  );
};
